//Types
import { is_weather_cond_ui, type WeatherConditionUI } from 'src/types/WeatherConditionUI';
import type { DeviceConditionUI, DeviceTriggerUI } from 'src/types/DeviceCondAndTriggerUI';
import type { TimeBasedConditionsUI } from 'src/types/TimeBasedConditionsUI';
import type { AutomationConditionUI } from 'src/types/AutomationConditionUI';
import type { SunriseSunsetConditionUI } from 'src/types/SunriseSunsetConditionUI';
import type { EnergyExchangeConditionUI } from 'src/types/EnergyExchangeConditionUI';
import type { MathConditionUI } from 'src/types/Scenes/MathConditionUI';
import type { NotificationActionUI } from 'src/types/NotificationActionUI';
import type { DeviceActionUI } from 'src/types/DeviceActionUI';
import type { SceneActionUI } from 'src/types/SceneActionUI';
import type { GroupActionUI } from 'src/types/GroupActionUI';
import type { RunScriptActionUI } from 'src/types/RunScriptActionUI';
import type { ISceneObj } from 'src/types/SceneType';
//Utils
import { _deepClone } from 'src/helpers/utilities';
import { SCENE_WEATHER_VALUE_TO_UI } from 'src/components/Scenes/helpers/utilities_scenes_to_ui';
//Helpers
import { conditionInterpretator } from 'src/components/Scenes/helpers/scene_conditions_to_ui';
import { extract_conditions_variables } from 'src/components/Scenes/components/GenericView/extract_conditions_variables';
import { extract_actions_variables } from 'src/components/Scenes/components/GenericView/extract_actions_variables';
import { actionInterpretator } from 'src/components/Scenes/helpers/scene_actions_to_ui';
import {
	getDelayedSceneAction,
	getSceneActionPurpose,
} from 'src/components/Scenes/helpers/get_delayed_scene_action';
import { hasRadioFavorites } from 'src/components/Scenes/helpers/radio_favorites_cache';
//Stores
import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';
//Services
import { format } from 'svelte-i18n';
//Helpers
import { isWallDisplay, createDeviceID } from 'src/helpers/interface_helpers';

const _format = get(format);

/**
 * @description
 * This function takes a scene as an argument and returns an array of condition objects
 * @param {Object} data - the scene
 * @returns {Array<Object>} an array of condition objects
 */
const getWhens = (data) => {
	return (
		data?.if?.or ||
		data?.if?.and?.find((el) => el?.or && el.or?.every((el) => !el.indailyint))?.or ||
		[]
	);
};

/**
 * @description
 * This function takes a weather condition object and checks if it's valid
 * @param {Object} data - the translated weather condition object
 * @returns {Boolean} true if the condition is invalid, false otherwise
 */
export const isInvalidWeatherCondition = ({
	value,
	condition,
}: {
	value: string;
	condition: string;
}) => {
	return value !== undefined && condition === 'clouds' && !(value in SCENE_WEATHER_VALUE_TO_UI);
};

/**
 * @description
 * This function takes a condition object and a scene as arguments and checks if the condition is a translated weather condition
 * If it is, it adds the type of missing data to the scene's missingData array
 * @param {Object} element - the condition object
 * @param {Object} scene - the scene
 */
const checkWhen = (element, scene) => {
	const _scene = _deepClone({ ...scene }) as ISceneObj;
	const purpose: string | undefined = element._gui_type || element._gui_purpose || undefined;

	const translated:
		| DeviceConditionUI
		| DeviceTriggerUI
		| TimeBasedConditionsUI
		| AutomationConditionUI
		| SunriseSunsetConditionUI
		| WeatherConditionUI
		| EnergyExchangeConditionUI
		| MathConditionUI
		| undefined = conditionInterpretator(element, purpose);

	const isWeatherCondition = is_weather_cond_ui(translated) && translated?.cond === 'clouds';
	const { title } = !isWeatherCondition && extract_conditions_variables(translated);

	const conditionFromOnce = element?.thsval; //element thsval is when trigger is at once
	const conditionFromAny = element?.eq?.[1]; //element eq is when trigger is at any change

	const condition = conditionFromOnce || conditionFromAny;

	if (title?.includes(_format('missing_device_label', { default: 'Missing Device' }))) {
		if (typeof _scene.missingData === 'undefined') {
			_scene.missingData = [];
		}

		_scene.missingData.push('hasMissingDevice');
	}

	if (
		isWeatherCondition &&
		isInvalidWeatherCondition({ condition: translated?.cond, value: condition })
	) {
		if (typeof _scene.missingData === 'undefined') {
			_scene.missingData = [];
		}
		_scene.missingData.push('hasMissingWeatherCondition');
	}

	return _scene;
};

/**
 * @description
 * This function takes an action object and a scene as arguments and checks if the action is a translated weather action
 * If it is, it adds the type of missing data to the scene's missingData array
 * @param {Object} element - the action object
 * @param {Object} scene - the scene
 */
const checkThen = (element, scene) => {
	const _scene = _deepClone({ ...scene }) as ISceneObj;
	if (!element) {
		return _scene;
	}

	const delayedAction = getDelayedSceneAction(element);
	const msg_type = element.msg_type;
	const purpose = getSceneActionPurpose(element, delayedAction);

	if (delayedAction !== undefined) {
		element = { ...delayedAction, delay: element.wait, units: element.units };
	}
	let translated:
		| NotificationActionUI
		| DeviceActionUI
		| SceneActionUI
		| GroupActionUI
		| RunScriptActionUI
		| undefined = actionInterpretator(purpose, element, msg_type);

	const { title } = extract_actions_variables(translated);

	if (title?.includes(_format('missing_device_label', { default: 'Missing Device' }))) {
		if (typeof _scene.missingData === 'undefined') {
			_scene.missingData = [];
		}
		_scene.missingData.push('hasMissingDevice');
	}

	const _devices = get(devices);
	if (translated && 'device' in translated && translated.device) {
		const device_id = createDeviceID(
			translated.device.id,
			translated.device.channel,
			_devices[translated.device.id]?.type,
		);
		const device = _devices[device_id];
		const isWallDisplayMediaPlayer =
			device?.category === 'media_player' && isWallDisplay(device?.type);
		const hasRadioPlayFavouriteAction = translated.actions?.some(
			(action) => action.name === 'Radio_PlayFavourite',
		);

		if (isWallDisplayMediaPlayer && hasRadioPlayFavouriteAction && device) {
			const radioFavoritesExist = hasRadioFavorites(device_id);
			if (radioFavoritesExist === false) {
				if (typeof _scene.missingData === 'undefined') {
					_scene.missingData = [];
				}
				if (!_scene.missingData.includes('hasNoFavoriteRadio')) {
					_scene.missingData.push('hasNoFavoriteRadio');
				}
			}
		}
	}

	return _scene;
};

/**
 * @description
 * This function takes a scene as an argument and returns it with an added property named "missingData" which is an array of strings.
 * The array contains the types of missing data in the scene.
 * The function checks for the following types of missing data:
 *  - "hasMissingDevice"
 *  - "hasMissingWeatherCondition"
 *  - "hasNoFavoriteRadio"
 * @param {Object} data - the scene to be checked
 * @returns {Object} the scene with the added "missingData" property
 */
export const hasMissingSceneData = (data) => {
	let scene: ISceneObj = _deepClone({ ...data }) as ISceneObj;

	const whens = getWhens(data);
	const thens = data?.do || [];

	whens.forEach((when) => {
		when?.and?.forEach((element) => {
			try {
				scene = { ...scene, ...checkWhen(element, scene) };
			} catch (error) {
				console.error('Failed to inspect scene condition for missing data', error);
			}
		});
	});

	thens.forEach((element) => {
		try {
			scene = { ...scene, ...checkThen(element, scene) };
		} catch (error) {
			console.error('Failed to inspect scene action for missing data', error);
		}
	});

	if (!scene.missingData || scene.missingData.length < 1) {
		delete scene.missingData;
	}

	return scene;
};
